import { Settings } from "luxon"
import React, { useContext, createContext, useState, useCallback, useMemo, useEffect } from "react"

import {
  DEFAULT_LOCALE,
  loadStoredLocale,
  persistLocale,
  resolveInitialLocale,
  type AppLocale,
} from "@/constants/locales"
import I18n from "@/i18n"

type LocaleContextValue = {
  locale: AppLocale
  setLocale: (locale: string) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)
export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider")
  }
  return context
}

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState<AppLocale>(() =>
    resolveInitialLocale({
      storedLocale: loadStoredLocale(),
      fallbackLocale: I18n.locale,
    }),
  )

  const applyLocale = useCallback((nextLocale: string) => {
    const resolvedLocale = persistLocale(nextLocale)
    I18n.locale = resolvedLocale
    Settings.defaultLocale = resolvedLocale

    return resolvedLocale
  }, [])

  const setLocale = useCallback(
    (nextLocale: string) => {
      const resolvedLocale = applyLocale(nextLocale)

      setLocaleState((currentLocale) => (currentLocale === resolvedLocale ? currentLocale : resolvedLocale))
    },
    [applyLocale],
  )

  useEffect(() => {
    applyLocale(locale || DEFAULT_LOCALE)
  }, [applyLocale, locale])

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale])

  return (
    <LocaleContext.Provider value={value} key={locale}>
      {children}
    </LocaleContext.Provider>
  )
}
